/**
 * Progress.jsx — reader/scribe progress page.
 *
 * Feb 14 2026 — replaced the legacy `StatsPanel + GamificationPanel`
 * (reader path) and `WriterProgress` (scribe path) with the same
 * StreakHeroCard + SmarterHubStats pattern that already ships on
 * `/stats` and `/profile/stats`. This kills the last traces of the
 * old evolve-substat / XP-only cards per user request: *"Replace
 * every trace of all the old stats with the new stats panel."*
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp } from 'lucide-react';
import StreakHeroCard from '../components/StreakHeroCard';
import SmarterHubStats from '../components/SmarterHubStats';
import { checkAuth } from '../utils/authUtils';
import {
  effectiveRoleFor,
  getActiveSpace,
  onSpaceChange,
} from '../utils/spaceContext';

const Progress = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [authError, setAuthError] = useState('');
  const [authRefresh, setAuthRefresh] = useState(0);
  // Re-render whenever the persona toggle flips (admin viewing-as, or
  // scribe↔reader space) so the streak card + panel swap in-place.
  const [, setSpaceTick] = useState(0);

  useEffect(() => onSpaceChange(() => setSpaceTick((n) => n + 1)), []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setAuthError('');
    (async () => {
      try {
        const u = await checkAuth();
        if (!u) { navigate('/login'); return; }
        if (active) setUser(u);
      } catch (_) {
        if (active) setAuthError('Your progress space is unavailable right now.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [navigate, authRefresh]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center animate-pulse">
          <TrendingUp className="w-7 h-7 text-white" />
        </div>
      </div>
    );
  }

  if (authError) {
    return (
      <main className="max-w-xl mx-auto px-4 py-12 text-center" role="alert" data-testid="progress-auth-error">
        <TrendingUp className="w-8 h-8 mx-auto mb-3 text-amber-300" />
        <h1 className="text-xl font-bold text-white">Progress unavailable</h1>
        <p className="mt-2 text-sm text-slate-400">{authError}</p>
        <button type="button" onClick={() => setAuthRefresh((value) => value + 1)} className="mt-5 rounded-lg border border-amber-300/40 px-4 py-2 text-sm font-bold text-amber-100 hover:bg-amber-300/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300">Retry</button>
      </main>
    );
  }

  const effective = effectiveRoleFor(user?.role || 'user', getActiveSpace());
  const isWriter = effective === 'writer';
  const isAdmin = user?.role === 'admin';
  const streakRole = isWriter ? 'writer' : 'reader';

  return (
    <main
      className="max-w-7xl mx-auto px-3 sm:px-4 py-6 sm:py-8"
      data-testid="progress-page"
    >
      <p className="eyebrow text-amber-300">Your activity</p>
      <h1
        className="font-display text-2xl sm:text-3xl font-black tracking-tight text-white mb-1"
        data-testid="progress-heading"
      >
        {isWriter ? 'Writer Progress' : 'Reader Progress'}
      </h1>
      <p className="text-xs sm:text-sm text-gray-400 mb-6" data-testid="progress-subheading">
        {isWriter
          ? 'Your server-calculated writing activity, rewards, and milestones.'
          : 'Your server-calculated reading activity, rewards, and milestones.'}
      </p>

      <section className="space-y-6" aria-label="Progress details">
        <StreakHeroCard role={streakRole} />
        <SmarterHubStats isAdmin={isAdmin} allowToggle />
      </section>
    </main>
  );
};

export default Progress;
